import React from 'react'
import { Stack } from '@mui/material'
import { AsYouType } from 'libphonenumber-js'

import { spacing } from '~tokens/internal'

import TextField, {
  ErrorText,
  type TextFieldBaseProps,
  type TextFieldContainerProps,
  TextFieldLabel,
} from './TextField'

interface PhoneFieldProps extends TextFieldContainerProps {
  disabled?: boolean
  numberProps: Omit<TextFieldBaseProps, 'disabled' | 'onChange'> & {
    onChange: (value: string) => void
  }
  extensionProps: Omit<TextFieldBaseProps, 'disabled' | 'type' | 'onChange'> & {
    onChange: (value: string) => void
  }
}

const PhoneField: React.FC<PhoneFieldProps> = ({
  label,
  required,
  error,
  errorText,
  disabled,
  containerStyle,
  InputLabelProps,
  numberProps: { value: numberValue, onChange: onNumberChange, ...numberRest },
  extensionProps: { onChange: onExtensionChange, ...extensionRest },
}) => {
  const formattedNumber = new AsYouType('US').input(numberValue as string)

  return (
    <Stack spacing={spacing.xxs} sx={containerStyle}>
      {label && (
        <TextFieldLabel
          label={label}
          required={required}
          disabled={disabled}
          InputLabelProps={InputLabelProps}
        />
      )}
      <Stack direction='row' spacing={spacing.xxs}>
        <TextField
          disabled={disabled}
          error={Boolean(error)}
          placeholder='(XXX) XXX-XXXX'
          value={formattedNumber}
          onChange={(e) => {
            const value = e.target.value
            // Digits without leading 0 or 1 (not allowed for US numbers), max of 10
            const normalizedDigits = value.replace(/\D/g, '').replace(/^[01]/, '').slice(0, 10)
            // Character(s) deleted from formatted number
            if (value.length < formattedNumber.length) {
              // Check if multiple digits or one was deleted
              const rawDigits = numberValue as string
              onNumberChange(
                normalizedDigits.length < rawDigits.length
                  ? normalizedDigits
                  : rawDigits.slice(0, -1),
              )
              // Character(s) added to formatted number
            } else {
              onNumberChange(normalizedDigits)
            }
          }}
          {...numberRest}
        />
        <TextField
          disabled={disabled}
          placeholder='Ext.'
          type='number'
          onChange={(e) => onExtensionChange(e.target.value)}
          {...extensionRest}
          sx={{ width: '80px' }}
        />
      </Stack>
      {error && errorText && <ErrorText error={errorText} />}
    </Stack>
  )
}

export default PhoneField
